import { isNonEmptyArray, isNonEmptyString, isObject } from '../utils/checks'
import { SponsorTierModule } from './sponsorTier'
import { algoliaIndex } from '../lib/algolia'
import logger from '../utils/logger'

export const isSponsor = (brand: { brandSponsorTier: number | null }) =>
  SponsorTierModule.isSponsor(brand?.brandSponsorTier)

export const isPPV = (brand: { brandSponsorTier: number | null }) =>
  SponsorTierModule.isPPV(brand?.brandSponsorTier)

export const isCapped = (brand: { brandSponsorTier: number | null }) => {
  if (!isObject(brand)) return false
  return SponsorTierModule.isCapped(brand?.brandSponsorTier)
}

export const isPaused = (brand: { brandSponsorTier: number | null }) => {
  if (!isObject(brand)) return false
  return SponsorTierModule.isPaused(brand?.brandSponsorTier)
}

export const BRAND_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  HIDDEN: 'Hidden'
}

export const getTitle = (brand) => {
  return brand?.title || ''
}

export const getSlug = (brand) => {
  return brand?.slug || ''
}

export const getBrandProfileSlug = (brand) => {
  const slug = getSlug(brand)
  return `/brand/${slug}/`
}

export const getLogo = (brand) => {
  if (isNonEmptyString(brand?.logo)) {
    return brand.logo
  }
  return ''
}

export const getFavicon = (brand?: { favicon?: string }): string => {
  if (isNonEmptyString(brand?.favicon)) {
    return brand.favicon
  }
  return ''
}

export const getWebsite = (brand) =>
  isNonEmptyString(brand?.website) ? brand.website : ''

export const hasWebsite = (brand) => isNonEmptyString(getWebsite(brand))

export const getPhoneNumbers = (brand) => {
  if (isObject(brand) && isNonEmptyArray(brand.phoneNumbers)) {
    return brand?.phoneNumbers
  }
  return []
}

export const getEmail = (brand) => {
  return brand?.email || ''
}

export const getSocialMedia = (brand) => {
  if (!isObject(brand)) return {}

  return {
    facebook: brand?.facebook || '',
    twitter: brand?.twitter || '',
    instagram: brand?.instagram || '',
    tiktok: brand?.tiktok || ''
  }
}

export const getYearFounded = (brand) => brand?.yearFounded || null

export const getLongDescription = (brand) => brand?.longDescription || ''

export const getCEO = (brand) => brand?.ceo || ''

export const getHeadquartersAddress = (brand) =>
  brand?.headquartersAddress || ''

export const getStockSymbol = (brand) => brand?.stockSymbol || ''

export const getVideoEmbed = (brand) => brand?.videoEmbed || ''

export const getParentBrandId = (brand) => brand?.parentId || null

export const hasCenters = (brand) =>
  isNonEmptyArray(brand?.centers) || brand?._count?.centers > 0

export const getCentersCount = (brand) =>
  isNonEmptyArray(brand?.centers)
    ? brand.centers.length
    : brand?._count?.centers || 0

export const getPublishedCentersCount = async (brand) => {
  try {
    if (!brand?.title) {
      throw new Error('Brand title not available for Algolia search')
    }

    const searchResult = await algoliaIndex.search('', {
      filters: `brand_title:"${brand.title}" AND NOT status:"Closed"`,
      hitsPerPage: 0,
      attributesToRetrieve: []
    })

    return searchResult.nbHits || 0
  } catch (error) {
    logger.warn({
      message:
        'Algolia failed for getPublishedCentersCount, using GraphQL fallback',
      error
    })

    return isNonEmptyArray(brand?.centers)
      ? brand.centers.filter(
          (center) =>
            center.status === 'PUBLISHED' || center.status === 'Published'
        ).length
      : brand?._count?.publishedCenters || 0
  }
}

export const hasChildBrands = (brand) =>
  isNonEmptyArray(brand?.children) || brand?._count?.children > 0

export const getChildBrandsCount = (brand) =>
  isNonEmptyArray(brand?.children)
    ? brand.children.length
    : brand?._count?.children || 0

export const isParentBrand = (brand) => hasChildBrands(brand)

export const isChildBrand = (brand) =>
  brand?.parentId !== null && brand?.parentId !== undefined
